import React, { useState, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import makeAnimated from "react-select/animated";
import {
  serviceFetchCommonSymptoms,
  serviceFetchDoctorsCustomSymptoms,
  serviceAddCustomSymptomForDoctor,
  serviceSuggestLabTests,
} from "../services/api";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

const SymptomsInput = ({
  symptoms,
  setSymptoms,
  doctorId,
  setLabTestSuggestions,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]); // State to store fetched symptoms options
  const [isLoading, setIsLoading] = useState(false); // State to track loading status

  useEffect(() => {
    const fetchSymptoms = async () => {
      try {
        // Fetch common and custom symptoms and format them for react-select
        const commonResponse = await serviceFetchCommonSymptoms();
        let formattedCommon = [];
        if (commonResponse?.status) {
          formattedCommon = (commonResponse?.data || []).map((symptom) => ({
            value: symptom.symptom_id.toString(),
            label: symptom.symptom_name,
          }));
        }
        let formattedCustom = [];
        const customResponse = await serviceFetchDoctorsCustomSymptoms(
          doctorId
        );
        if (customResponse?.status) {
          formattedCustom = (customResponse?.data || []).map((symptom) => ({
            value: symptom.symptom_id.toString(),
            label: symptom.symptom_name,
          }));
        }
        setOptions([...formattedCommon, ...formattedCustom]);
      } catch (error) {
        console.error("Failed to fetch symptoms:", error);
      }
    };
    fetchSymptoms();
  }, [doctorId]);

  // Function to handle when new symptoms are added
  const handleInputChange = (newValue) => {
    setInputValue(newValue);
  };

  const suggestLabTests = async () => {
    setIsLoading(true);
    const complaints = symptoms.map((s) => s.label).join(", ");

    try {
      const response = await serviceSuggestLabTests(complaints);
      if (response?.status) {
        const tests = response.data;
        setLabTestSuggestions(tests);
      } else {
        console.error("Error suggesting lab tests:", response?.message);
      }
    } catch (error) {
      console.error("Error suggesting lab tests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle selection of symptoms
  const handleChange = async (selectedOptions, actionMeta) => {
    setSymptoms(selectedOptions);

    if (actionMeta.action === "create-option") {
      // Handle creation of new symptom
      const newSymptom = actionMeta.option.value;
      try {
        const savedSymptom = await serviceAddCustomSymptomForDoctor(doctorId, {
          name: newSymptom,
        });
        if (savedSymptom?.status) {
          // Add the new symptom to the options state
          const newSymptom = savedSymptom?.data;
          setOptions([
            ...options,
            { value: newSymptom.symptom_id.toString(), label: newSymptom.name },
          ]);
        }
      } catch (error) {
        console.error(`Failed to save custom symptom "${newSymptom}":`, error);
      }
    }
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "0.375rem", // Tailwind rounded-md
      padding: "0.5rem", // Tailwind p-2
      border: "1px solid #d1d5db", // Tailwind border-gray-300
      // Add more styles as needed
    }),
    // Define additional custom styles for other parts of the select component
  };

  const animatedComponents = makeAnimated();

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        Chief Complaints
      </h2>
      <hr className="mt-1 mb-4" />
      <CreatableSelect
        components={animatedComponents}
        inputValue={inputValue}
        value={symptoms}
        onInputChange={handleInputChange}
        onChange={handleChange}
        options={options}
        isMulti
        styles={customSelectStyles}
        placeholder="Select or type symptoms"
        classNamePrefix="react-select"
      />
      <button
        onClick={suggestLabTests}
        className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center"
        disabled={isLoading} // Disable the button while loading
      >
        {isLoading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Generating Suggestions...
          </>
        ) : (
          "Suggest Lab Tests"
        )}
      </button>
    </div>
  );
};

export default SymptomsInput;
